// MeetSuggestionCard — ticket/event recommendation from AI transcript analysis.
// Styled like a mini ticket card. Clicking opens the creation flow.

import { SUGGESTION_BUCKETS, fingerprintSuggestion, routeTo } from '../../functions/meet/meetUtils.js'

const _appliedEntry = (el, s) => {
  const applied = s?.root?.analysis?.applied_items || []
  if (!applied.length || !el?._fingerprint) return null
  return applied.find(a => a.fingerprint === el._fingerprint && a.bucket === s?.meetBucket) || null
}

const _isApplied = (el, s) => !!_appliedEntry(el, s)

const _openAppliedTarget = (el, s) => {
  const cfg = SUGGESTION_BUCKETS[s?.meetBucket]
  if (!cfg?.route) return
  const targetId = _appliedEntry(el, s)?.target_id
  if (targetId && cfg.stateKey && s?.root?.update) {
    s.root.update({ [cfg.stateKey]: targetId })
  }
  routeTo(el, cfg.route)
}

const _bucketIcon = (bucket) => {
  if (bucket === 'tickets') return '■'
  if (bucket === 'calendar_events') return '○'
  if (bucket === 'notes') return '◆'
  if (bucket === 'knowledge_base') return '◇'
  return '•'
}

const _bucketColor = (bucket) => {
  if (bucket === 'tickets') return 'orange'
  if (bucket === 'calendar_events') return 'blue'
  if (bucket === 'notes') return 'green'
  if (bucket === 'knowledge_base') return 'cyan'
  return 'caption'
}

export const MeetSuggestionCard = {
  flow: 'y',
  gap: 'Z',
  padding: 'A',
  round: 'A',
  borderWidth: '1px',
  borderColor: (el, s) => _isApplied(el, s) ? 'green.24' : 'line',
  borderStyle: 'solid',
  background: (el, s) => _isApplied(el, s) ? 'green.04' : 'white.04',
  cursor: 'pointer',
  position: 'relative',
  transition: 'transform 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
  ':hover': {
    transform: 'translateY(-2px)',
    boxShadow: (el, s) => _isApplied(el, s)
      ? '0 8px 24px -8px rgba(34,197,94,.25)'
      : '0 8px 24px -8px var(--color-shadow)'
  },

  onCreate: async (el, s) => {
    try {
      el._fingerprint = await fingerprintSuggestion(s || {})
    } catch { el._fingerprint = null }
  },

  onClick: (e, el, s) => {
    const bucket = s?.meetBucket
    if (!bucket) return
    if (_isApplied(el, s)) {
      _openAppliedTarget(el, s)
      return
    }
    el.call('openSuggestionModal', bucket, s)
  },

  // Top row: bucket icon + title + applied check
  TopRow: {
    flow: 'x',
    align: 'center left',
    gap: 'Z',

    BucketIcon: {
      tag: 'span',
      fontSize: 'Z2',
      color: (el, s) => _bucketColor(s?.meetBucket),
      text: (el, s) => _bucketIcon(s?.meetBucket)
    },

    Title: {
      tag: 'h4',
      margin: '0',
      fontSize: 'Z2',
      fontWeight: '600',
      color: (el, s) => _isApplied(el, s) ? 'caption' : 'title',
      textDecoration: (el, s) => _isApplied(el, s) ? 'line-through' : 'none',
      flex: '1',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s?.title || s?.body || 'Untitled'
    },

    Check: {
      hide: (el, s) => !_isApplied(el, s),
      tag: 'span',
      flow: 'x',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      background: 'green.12',
      color: 'green',
      fontSize: 'Y2',
      fontWeight: '700',
      text: '✓ Created'
    }
  },

  // Description
  Body: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    color: 'caption',
    lineHeight: '1.5',
    text: (el, s) => s?.description || s?.summary || s?.content || s?.body || ''
  },

  // Meta: date suggestion, assignee, tags
  Meta: {
    flow: 'x wrap',
    gap: 'Z',
    fontSize: 'Y2',
    align: 'center left',

    DateBadge: {
      display: (el, s) => s?.proposed_time || s?.date || s?.due_date ? 'inline-flex' : 'none',
      flow: 'x',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      background: 'blue.12',
      color: 'blue',
      fontWeight: '600',
      Icon: { extends: 'Icon', icon: 'clock', boxSize: 'Y2' },
      Text: {
        tag: 'span',
        text: (el, s) => s?.proposed_time || s?.date || s?.due_date || ''
      }
    },

    AssigneeBadge: {
      display: (el, s) => s?.assignee_email ? 'inline-flex' : 'none',
      flow: 'x',
      align: 'center center',
      gap: 'V',
      padding: 'V Z',
      round: 'C',
      background: 'overlay',
      color: 'caption',
      fontWeight: '500',
      text: (el, s) => s?.assignee_email ? `→ ${s.assignee_email.split('@')[0]}` : ''
    },

    Tags: {
      display: (el, s) => Array.isArray(s?.tags) && s.tags.length ? 'inline' : 'none',
      tag: 'span',
      color: 'caption',
      text: (el, s) => Array.isArray(s?.tags) ? s.tags.map(t => `#${t}`).join(' ') : ''
    }
  },

  // CTA button at bottom — create-state
  CreateBtn: {
    hide: (el, s) => _isApplied(el, s),
    tag: 'button',
    type: 'button',
    display: 'flex',
    align: 'center center',
    width: '100%',
    padding: 'Z',
    marginTop: 'V',
    fontSize: 'Y2',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    round: 'Z',
    background: (el, s) => `${_bucketColor(s?.meetBucket)}.12`,
    color: (el, s) => _bucketColor(s?.meetBucket),
    text: (el, s) => {
      const b = s?.meetBucket
      if (b === 'tickets') return 'Create ticket'
      if (b === 'calendar_events') return 'Create event'
      if (b === 'notes') return 'Create note'
      if (b === 'knowledge_base') return 'Add to KB'
      return 'Create'
    },
    ':hover': { opacity: '.85' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      if (_isApplied(el, s)) return
      const bucket = s?.meetBucket
      if (!bucket) return
      el.call('openSuggestionModal', bucket, s)
    }
  },

  // Applied-state bar — takes CreateBtn's slot, deep-links to the target
  ViewBtn: {
    hide: (el, s) => !_isApplied(el, s),
    tag: 'button',
    type: 'button',
    display: 'flex',
    align: 'center center',
    width: '100%',
    padding: 'Z',
    marginTop: 'V',
    fontSize: 'Y2',
    fontWeight: '600',
    border: 'none',
    cursor: 'pointer',
    round: 'Z',
    background: 'green.12',
    color: 'green',
    text: (el, s) => SUGGESTION_BUCKETS[s?.meetBucket]?.viewLabel || 'View →',
    ':hover': { opacity: '.85' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      _openAppliedTarget(el, s)
    }
  }
}
